import { css, cx } from '@emotion/css';
import { theme } from 'antd';
import type React from 'react';

/** Segmented styling derived from the theme-driven header background, so it
 *  adapts to light and dark headers: a faint track plus an elevated pill
 *  (white on light, frosted on dark) and contrast-aware text. Falls back to the
 *  light treatment for non-hex / transparent backgrounds. */
const headerSegmentStyle = (hex?: string) => {
  const isDark = (() => {
    if (hex && hex.length === 7) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return (r * 299 + g * 587 + b * 114) / 1000 < 128;
    }
    return false;
  })();

  return isDark
    ? {
        fg: 'rgba(255, 255, 255, 0.82)',
        track: 'rgba(255, 255, 255, 0.08)',
        hoverBg: 'rgba(255, 255, 255, 0.1)',
        selBg: 'rgba(255, 255, 255, 0.3)',
        selColor: '#ffffff',
        selShadow: 'none',
      }
    : {
        fg: 'rgba(0, 0, 0, 0.7)',
        track: 'rgba(0, 0, 0, 0.05)',
        hoverBg: 'rgba(255, 255, 255, 0.35)',
        selBg: 'rgba(255, 255, 255, 0.68)',
        selColor: 'rgba(0, 0, 0, 0.88)',
        selShadow: '0 1px 3px rgba(0, 0, 0, 0.14)',
      };
};

export const AppSwitcher: React.FC = () => {
  const { token } = theme.useToken();

  if (window._env_.ENV_TAG === 'prod') {
    return (
      <span
        className={css`
          font-weight: 500;
          font-size: 20px;
        `}
      >
        Anforderungen
      </span>
    );
  }

  const { fg, track, hoverBg, selBg, selColor, selShadow } = headerSegmentStyle(token.Layout?.headerBg);

  const trackStyle = css`
    display: inline-flex;
    gap: 2px;
    padding: 3px;
    border-radius: 10px;
    margin: 0 ${token.marginLG}px;
    background-color: ${track};

    @media (max-width: ${token.screenMD}px) {
      display: none;
    }
  `;

  const itemStyle = css`
    appearance: none;
    border: 0;
    background: transparent;
    cursor: pointer;
    padding: 6px 16px;
    border-radius: 8px;
    font-family: inherit;
    font-size: ${token.fontSizeLG}px;
    font-weight: 500;
    line-height: 1.4;
    color: ${fg};
    transition:
      color 0.15s,
      background-color 0.15s;

    &:hover {
      background-color: ${hoverBg};
    }
  `;

  const selectedStyle = css`
    cursor: default;
    color: ${selColor};
    background-color: ${selBg};
    box-shadow: ${selShadow};

    /* keep the active pill stable on hover (it also carries itemStyle) */
    &:hover {
      background-color: ${selBg};
    }
  `;

  return (
    <div className={trackStyle} role="tablist">
      <button type="button" className={cx(itemStyle, selectedStyle)} role="tab" aria-selected="true">
        Anforderungen
      </button>
      <button
        type="button"
        className={itemStyle}
        role="tab"
        aria-selected="false"
        onClick={() => {
          window.location.href = window._env_.BEFUNDE_URL;
        }}
      >
        Befunde
      </button>
    </div>
  );
};
